/**
 * Smooth open and close for statement-style accordions.
 *
 * The theme animates <details> with `interpolate-size` and `@starting-style`,
 * which only Chromium honours — Safari and Firefox snap open. This drives the
 * same movement from the Web Animations API instead, so the FAQ opens the same
 * way everywhere. It only touches accordions marked `.accordion--statement`;
 * every other accordion keeps the theme's own behaviour.
 *
 * Adding `accordion--js-motion` is what switches off the CSS transition, so if
 * this file never loads the accordion still opens — just without the easing.
 */

const OPEN_MS = 420;
const CLOSE_MS = 340;
const EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function markAccordions() {
  for (const accordion of document.querySelectorAll('.accordion--statement')) {
    accordion.classList.add('accordion--js-motion');
  }
}

/**
 * Animates one row's content between closed and open.
 * @param {HTMLDetailsElement} details
 * @param {HTMLElement} content
 */
function toggle(details, content) {
  for (const animation of content.getAnimations()) animation.cancel();

  const styles = getComputedStyle(content);
  const paddingStart = styles.paddingBlockStart;
  const paddingEnd = styles.paddingBlockEnd;
  const closed = {
    height: '0px',
    paddingBlockStart: '0px',
    paddingBlockEnd: '0px',
    opacity: 0,
  };

  content.style.overflow = 'hidden';

  if (!details.open) {
    details.classList.remove('is-closing');
    details.open = true;

    const open = {
      height: `${content.scrollHeight}px`,
      paddingBlockStart: paddingStart,
      paddingBlockEnd: paddingEnd,
      opacity: 1,
    };

    content
      .animate([closed, open], { duration: OPEN_MS, easing: EASING })
      .finished.catch(() => {})
      .finally(() => {
        content.style.overflow = '';
      });
    return;
  }

  const open = {
    height: `${content.scrollHeight}px`,
    paddingBlockStart: paddingStart,
    paddingBlockEnd: paddingEnd,
    opacity: 1,
  };

  // `open` has to stay on until the collapse finishes, or the content would
  // vanish mid-animation — so the icon is told to turn from this class instead
  // and moves with the click rather than after it.
  details.classList.add('is-closing');

  content
    .animate([open, closed], { duration: CLOSE_MS, easing: EASING })
    .finished.then(() => {
      details.open = false;
    })
    .catch(() => {})
    .finally(() => {
      details.classList.remove('is-closing');
      content.style.overflow = '';
    });
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const summary = target.closest('.accordion--statement summary');
  if (!summary) return;

  const details = summary.closest('details');
  const content = details?.querySelector('.details-content');
  if (!details || !content || !content.animate || prefersReducedMotion()) return;

  // Take over from the browser so the height can be animated.
  event.preventDefault();
  toggle(details, content);
});

markAccordions();
document.addEventListener('DOMContentLoaded', markAccordions);
document.addEventListener('shopify:section:load', markAccordions);
